import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Eye, ArrowLeft, Trophy, History, Heart, Play, RotateCcw, AlertCircle
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useMobileView } from "@/hooks/use-mobile-view";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SUBJECTS = ['man1', 'man2', 'woman1', 'woman2', 'boy1', 'girl1'];
const EMOTIONS = ['sad', 'fear', 'happy', 'anger', 'disgust', 'surprise'];
const MAX_SPEED_BONUS = 50;

export default function MicroExpressionPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const isMobile = useMobileView();
  
  // Game State
  const [gameState, setGameState] = useState('intro'); // intro, loading, baseline, flash, input, feedback, gameover
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [currentRound, setCurrentRound] = useState(null); // { subject, emotion, neutralSrc, emotionSrc }
  const [inputStartTime, setInputStartTime] = useState(0);
  
  // Data
  const [leaderboard, setLeaderboard] = useState([]);
  const [attempts, setAttempts] = useState([]);

  // Refs
  const timeoutRef = useRef(null);
  const neutralImgRef = useRef(null);
  const emotionImgRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const savedUser = localStorage.getItem('user');
    if (savedUser) setUser(JSON.parse(savedUser));
    
    fetchLeaderboard();
    fetchAttempts();
    
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/micro-expression/leaderboard`);
      setLeaderboard(response.data);
    } catch (e) {
      console.error('Failed to fetch leaderboard', e);
    }
  };

  const fetchAttempts = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/micro-expression/results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttempts(response.data);
    } catch (e) {
      console.error('Failed to fetch attempts', e);
    }
  };

  const getFlashDuration = (lvl) => {
    if (lvl <= 3) return 500;
    if (lvl <= 7) return 300;
    if (lvl <= 12) return 150;
    if (lvl <= 20) return 100;
    return 50;
  };

  const startGame = () => {
    setScore(0);
    setLives(3);
    setLevel(1);
    startRound(1);
  };

  const startRound = (lvl) => {
    setGameState('loading');
    
    // Pick Random Subject and Emotion
    const subject = SUBJECTS[Math.floor(Math.random() * SUBJECTS.length)];
    const emotion = EMOTIONS[Math.floor(Math.random() * EMOTIONS.length)];
    
    const neutralSrc = `/assets/image/faces/${subject}/neutral.png`;
    const emotionSrc = `/assets/image/faces/${subject}/${emotion}.png`;
    
    // Preload Images
    const neutralImg = new Image();
    const emotionImg = new Image();
    
    let loadedCount = 0;
    const onLoaded = () => {
        loadedCount++;
        if (loadedCount === 2) {
            // Once both are loaded, start the 3s countdown sequence
            // We set the currentRound state, which triggers the useEffect
            // But we need to ensure the sequence doesn't start until we are ready
            // The useEffect below handles the start when loading is done
            
            // We keep the state update here to transition out of the spinner
            setCurrentRound({ subject, emotion, neutralSrc, emotionSrc });
        }
    };
    
    neutralImg.onload = onLoaded;
    emotionImg.onload = onLoaded;
    
    // Handle error - fallback to png if jpg fails? Or assume correct paths from prompt.
    // Prompt says adjust extension if necessary. Assuming jpg for now as per prompt.
    neutralImg.onerror = (e) => { console.error("Failed to load image", neutralSrc); toast.error("Image load error"); };
    emotionImg.onerror = (e) => { console.error("Failed to load image", emotionSrc); toast.error("Image load error"); };

    neutralImg.src = neutralSrc;
    emotionImg.src = emotionSrc;
    
    // Store refs to keep in memory? Browser cache should handle it once loaded.
    neutralImgRef.current = neutralImg;
    emotionImgRef.current = emotionImg;
  };

  useEffect(() => {
    // Watch for currentRound change AND loading state to trigger sequence safely
    if (currentRound && gameState === 'loading') {
        // Ensure images are actually ready in the DOM before sequence starts
        // We use a small timeout to allow React render cycle to complete and paint the hidden images
        const sequenceTimeout = setTimeout(() => {
            runSequence(level);
        }, 100);
        return () => clearTimeout(sequenceTimeout);
    }
  }, [currentRound, gameState]);

  const runSequence = (lvl) => {
    // 1. Baseline (1.5 - 2.5s)
    setGameState('baseline');
    // Hide buttons during baseline
    
    // Updated to 3000ms countdown as requested
    const baselineDuration = 3000;
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
        // 2. Flash
        setGameState('flash');
        const flashDuration = getFlashDuration(lvl);
        
        timeoutRef.current = setTimeout(() => {
            // 3. Mask (Back to neutral) & Input
            setGameState('input');
            setInputStartTime(Date.now());
        }, flashDuration);
    }, baselineDuration);
  };

  const handleInput = (selectedEmotion) => {
    if (gameState !== 'input') return;
    
    const isCorrect = selectedEmotion === currentRound.emotion;
    
    if (isCorrect) {
        // Scoring
        const reactionTime = (Date.now() - inputStartTime) / 1000;
        const speedBonus = reactionTime < 1 ? MAX_SPEED_BONUS : Math.max(0, MAX_SPEED_BONUS - Math.floor((reactionTime - 1) / 0.5) * 10);
        const roundScore = (level * 100) + speedBonus;
        
        setScore(prev => prev + roundScore);
        toast.success(`Correct! +${roundScore} pts`);
        
        setGameState('feedback');
        setTimeout(() => {
            const nextLvl = level + 1;
            setLevel(nextLvl);
            startRound(nextLvl);
        }, 1000);
    } else {
        // Wrong
        const newLives = lives - 1;
        setLives(newLives);
        toast.error(`Wrong! It was ${currentRound.emotion.toUpperCase()}`);
        
        setGameState('feedback');
        if (newLives <= 0) {
            setTimeout(endGame, 1000);
        } else {
            setTimeout(() => {
                const nextLvl = level + 1; // Still progress level? Prompt says "Move to the next level with a new random subject"
                setLevel(nextLvl);
                startRound(nextLvl);
            }, 1500);
        }
    }
  };

  const endGame = async () => {
    setGameState('gameover');
    if (user) {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/micro-expression/results`, {
                score: score,
                level_reached: level
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchLeaderboard();
            fetchAttempts();
        } catch (e) {
            console.error("Failed to save score", e);
        }
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-white font-sans selection:bg-orange-500/30">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 p-4 flex justify-between items-center bg-[#020617]/90 backdrop-blur border-b border-slate-800">
        <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5 mr-2" /> Exit
            </Button>
            <div className="text-sm text-slate-400 hidden sm:block">
                {user?.username}
            </div>
        </div>
        <div className="flex gap-6 font-mono text-sm">
           <div className="flex items-center gap-2">
             <Trophy className="w-4 h-4 text-yellow-500" />
             <span className="text-xl font-bold">{score}</span>
           </div>
           <div className="flex items-center gap-2 text-red-500">
             <Heart className="w-4 h-4 fill-current" />
             <span className="text-xl font-bold">{lives}</span>
           </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-3'} gap-8`}>
            
            {/* Game Area */}
            <div className={`${isMobile ? 'w-full' : 'lg:col-span-2'}`}>
                <div className="flex flex-col items-center justify-center p-6 relative w-full bg-slate-900/30 rounded-3xl border border-slate-800/50 min-h-[600px]">
                    
                    {/* Intro State */}
                    {gameState === 'intro' && (
                        <div className="text-center space-y-8 animate-in fade-in zoom-in duration-500">
                            <div className="w-24 h-24 bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl shadow-orange-500/20">
                                <Eye className="w-12 h-12 text-white" />
                            </div>
                            <div>
                                <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">MICRO EXPRESSION</h1>
                                <p className="text-slate-400 text-lg max-w-xs mx-auto">
                                    Detect the hidden emotion. <br/>
                                    Blink and you'll miss it.
                                </p>
                            </div>
                            <Button onClick={startGame} className="w-full py-8 text-xl font-bold bg-white text-black hover:bg-slate-200 rounded-xl uppercase tracking-wider shadow-xl shadow-white/10">
                                Start Test
                            </Button>
                        </div>
                    )}

                    {/* Game Over State */}
                    {gameState === 'gameover' && (
                        <div className="text-center space-y-8 animate-in zoom-in duration-500">
                            <div className="text-6xl mb-4">💀</div>
                            <h2 className="text-4xl font-black">GAME OVER</h2>
                            
                            <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800 w-full max-w-xs mx-auto space-y-4">
                                <div>
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Final Score</div>
                                    <div className="text-5xl font-mono font-bold text-white">{score}</div>
                                </div>
                                <div className="border-t border-slate-800 pt-4">
                                    <div className="text-slate-500 text-sm uppercase tracking-wider mb-1">Level Reached</div>
                                    <div className="text-2xl font-mono font-bold text-orange-400">{level}</div>
                                </div>
                            </div>

                            <Button onClick={startGame} className="w-full py-6 px-8 text-lg font-bold bg-white text-black hover:bg-slate-200">
                                <RotateCcw className="w-5 h-5 mr-2" /> Play Again
                            </Button>
                        </div>
                    )}

                    {/* Active Game Viewport */}
                    {(gameState === 'loading' || gameState === 'baseline' || gameState === 'flash' || gameState === 'input' || gameState === 'feedback') && (
                        <div className="w-full max-w-4xl space-y-4 animate-in fade-in">
                            <div className="text-center flex justify-between items-end px-4">
                                <div className="text-orange-400 font-mono text-sm uppercase tracking-widest">Level {level}</div>
                                <div className="text-slate-500 text-xs uppercase tracking-widest">
                                    Flash: {getFlashDuration(level)}ms
                                </div>
                            </div>
                            
                            <div className={`flex ${isMobile ? 'flex-col' : 'flex-row'} gap-6 items-center justify-center`}>
                                {/* Image Container */}
                                <div className={`relative ${isMobile ? 'w-full max-w-xs' : 'flex-1 max-w-md'} aspect-[256/279] bg-black rounded-2xl overflow-hidden border-2 border-slate-800 shadow-2xl`}>
                                    {gameState === 'loading' && (
                                        <div className="absolute inset-0 flex items-center justify-center z-50 bg-black">
                                            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                    
                                    {currentRound && (
                                        <>
                                            {/* Baseline Countdown Overlay */}
                                            {gameState === 'baseline' && (
                                                <div className="absolute inset-0 flex items-center justify-center z-30 bg-black/50 backdrop-blur-[2px] transition-opacity duration-500 animate-out fade-out">
                                                    <div className="text-4xl font-black text-white/50 tracking-widest animate-pulse">
                                                        READY
                                                    </div>
                                                </div>
                                            )}

                                            {/* Neutral Image (Bottom Layer) */}
                                            <img 
                                                src={currentRound.neutralSrc}
                                                alt="Neutral"
                                                className="absolute inset-0 w-full h-full object-cover z-10"
                                                style={{ pointerEvents: 'none' }}
                                            />
                                            
                                            {/* Emotion Image (Top Layer - Toggled Visibility) */}
                                            <img 
                                                src={currentRound.emotionSrc}
                                                alt="Emotion"
                                                className={`absolute inset-0 w-full h-full object-cover z-20 ${gameState === 'flash' ? 'opacity-100' : 'opacity-0'}`}
                                                style={{ pointerEvents: 'none', transition: 'none' }} // Zero transition for instant flash
                                            />
                                        </>
                                    )}
                                </div>

                                {/* Input Area */}
                                <div className={`${isMobile ? 'w-full grid-cols-3' : 'w-48 grid-cols-1'} grid gap-3`}>
                                    {EMOTIONS.map(emotion => (
                                        <button
                                            key={emotion}
                                            onClick={() => handleInput(emotion)}
                                            disabled={gameState !== 'input'}
                                            className={`
                                                py-3 rounded-xl font-bold uppercase tracking-wider text-xs sm:text-sm transition-all transform
                                                ${gameState === 'input' 
                                                    ? 'bg-slate-800 hover:bg-slate-700 hover:scale-105 active:scale-95 text-white border border-slate-700' 
                                                    : 'bg-slate-900 text-slate-600 border border-slate-800 cursor-not-allowed opacity-50'}
                                            `}
                                        >
                                            {emotion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Leaderboard */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-heading font-bold text-lg">Global Leaderboard</h3>
                </div>
                <ScrollArea className="h-64">
                  {leaderboard.length > 0 ? (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            entry.username === user?.username ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {entry.rank}
                            </span>
                            <span className="font-medium truncate max-w-24">{entry.username}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-orange-400">{entry.score}</div>
                            <div className="text-xs text-slate-500">Lvl {entry.level_reached}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No entries yet. Be the first!
                    </div>
                  )}
                </ScrollArea>
              </div>

              {/* Recent Attempts */}
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-orange-400" />
                  <h3 className="font-heading font-bold text-lg">Your History</h3>
                </div>
                <ScrollArea className="h-64">
                  {attempts.length > 0 ? (
                    <div className="space-y-2">
                      {attempts.slice(0, 15).map((attempt, idx) => {
                        const prevAttempt = attempts[idx + 1];
                        const isImprovement = prevAttempt && attempt.score > prevAttempt.score;
                        const isWorst = prevAttempt && attempt.score < prevAttempt.score;
                        
                        return (
                          <div 
                            key={attempt.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                          >
                            <div className="flex items-center gap-2">
                              {isImprovement && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
                              {isWorst && <div className="w-2 h-2 rounded-full bg-red-500" />}
                              {!isImprovement && !isWorst && <div className="w-2 h-2 rounded-full bg-slate-600" />}
                              <span className="text-slate-400 text-sm">
                                {new Date(attempt.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="text-right">
                                <div className="font-mono font-bold text-orange-400">{attempt.score}</div>
                                <div className="text-xs text-slate-500">Lvl {attempt.level_reached}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-8">
                      No attempts yet. Start testing!
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
